import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core/useNotifications';
import { useTranslation } from 'react-i18next';
import { MutationConfig } from '@nohara/react-core';
import { UpdateUserDto } from '@nohara/oidc-core';

import { api } from '@/lib';

export const updateUser = async (userId: string, data: UpdateUserDto) => {
  await api.put(`/users/${userId}`, data);
};

export interface UseUpdateUserOptions {
  mutationConfig?: MutationConfig<(params: UpdateUserParams) => Promise<void>>;
}

export interface UpdateUserParams {
  userId: string;
  data: UpdateUserDto;
}

export const useUpdateUser = ({ mutationConfig }: UseUpdateUserOptions = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications();

  const { onSuccess, onError, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: ({ userId, data }: UpdateUserParams) => updateUser(userId, data),
    onSuccess: (...args) => {
      // Invalidate and refetch the user query
      queryClient.invalidateQueries({ queryKey: ['user', args[1].userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });

      notifications.show(t('users.update.success'), {
        severity: 'success',
      });
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.show(t('users.update.error'), {
        severity: 'error',
      });
      onError?.(...args);
    },
    ...restConfig,
  });
};
